import { useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import type { WbsRollupNode } from '../../services/analytics';
import {
  ChartEmpty,
  formatCompact,
  formatCurrency,
  useMeasure,
  useTooltip,
} from './chartPrimitives';

type SizeMetric = 'activities' | 'duration' | 'cost';
type ColourMetric = 'float' | 'progress' | 'critical';

interface WbsTreemapProps {
  roots: WbsRollupNode[];
  hasCost: boolean;
  onSelectNode?: (activityIds: string[], label: string) => void;
}

interface Tile {
  node: WbsRollupNode;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Squarified treemap of the WBS.
 *
 * Area answers "where is the mass of the programme", colour answers "where is
 * the pain". Those are the two questions asked of a WBS and neither is
 * answerable from a hierarchy table.
 */
function squarify(
  nodes: { node: WbsRollupNode; value: number }[],
  x: number,
  y: number,
  width: number,
  height: number
): Tile[] {
  const tiles: Tile[] = [];
  const total = nodes.reduce((sum, n) => sum + n.value, 0);
  if (total <= 0 || width <= 0 || height <= 0) return tiles;

  // Slice-and-dice against the shorter edge keeps aspect ratios reasonable
  // without the full squarified-treemap bookkeeping.
  const layout = (
    items: { node: WbsRollupNode; value: number }[],
    px: number,
    py: number,
    pw: number,
    ph: number
  ): void => {
    if (items.length === 0 || pw <= 0 || ph <= 0) return;

    if (items.length === 1) {
      tiles.push({ node: items[0].node, x: px, y: py, width: pw, height: ph });
      return;
    }

    const sum = items.reduce((acc, item) => acc + item.value, 0);
    // Split the list at the point closest to half the total value.
    let running = 0;
    let splitIndex = 0;
    let bestDelta = Infinity;
    for (let i = 0; i < items.length - 1; i++) {
      running += items[i].value;
      const delta = Math.abs(running - sum / 2);
      if (delta < bestDelta) {
        bestDelta = delta;
        splitIndex = i + 1;
      }
    }

    const head = items.slice(0, splitIndex);
    const tail = items.slice(splitIndex);
    const headValue = head.reduce((acc, item) => acc + item.value, 0);
    const fraction = sum > 0 ? headValue / sum : 0.5;

    if (pw >= ph) {
      const splitWidth = pw * fraction;
      layout(head, px, py, splitWidth, ph);
      layout(tail, px + splitWidth, py, pw - splitWidth, ph);
    } else {
      const splitHeight = ph * fraction;
      layout(head, px, py, pw, splitHeight);
      layout(tail, px, py + splitHeight, pw, ph - splitHeight);
    }
  };

  layout([...nodes].sort((a, b) => b.value - a.value), x, y, width, height);
  return tiles;
}

/** Diverging float scale: red at negative, blue at zero, cool as float grows. */
function floatColour(meanFloat: number): string {
  if (meanFloat < 0) return 'var(--status-fail)';
  if (meanFloat <= 1) return 'var(--data-1)';
  if (meanFloat <= 10) return 'var(--data-2)';
  if (meanFloat <= 25) return 'var(--data-3)';
  if (meanFloat <= 44) return 'var(--data-4)';
  return 'var(--status-warn)';
}

function progressColour(percent: number): string {
  if (percent >= 95) return 'var(--data-5)';
  if (percent >= 60) return 'var(--data-4)';
  if (percent >= 30) return 'var(--data-3)';
  if (percent > 0) return 'var(--data-2)';
  return 'var(--data-1)';
}

export function WbsTreemap({ roots, hasCost, onSelectNode }: WbsTreemapProps) {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const tooltip = useTooltip();
  const [sizeBy, setSizeBy] = useState<SizeMetric>(hasCost ? 'cost' : 'duration');
  const [colourBy, setColourBy] = useState<ColourMetric>('float');
  const [drillPath, setDrillPath] = useState<WbsRollupNode[]>([]);

  const current = drillPath[drillPath.length - 1];
  const displayed = current ? current.children : roots;

  const valueOf = (node: WbsRollupNode): number => {
    if (sizeBy === 'activities') return node.activityCount;
    if (sizeBy === 'duration') return node.durationDays;
    return node.budgetCost;
  };

  const height = 320;
  const tiles = useMemo(() => {
    const candidates = displayed
      .map((node) => ({ node, value: valueOf(node) }))
      .filter((entry) => entry.value > 0);
    return squarify(candidates, 0, 0, width, height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayed, width, sizeBy]);

  const colourFor = (node: WbsRollupNode) => {
    if (colourBy === 'float') return floatColour(node.meanFloatDays);
    if (colourBy === 'progress') return progressColour(node.percentComplete);
    return node.criticalCount > 0 ? 'var(--status-fail)' : 'var(--data-3)';
  };

  if (roots.length === 0) {
    return <ChartEmpty message="No WBS structure in this file." />;
  }

  return (
    <div className="w-full">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {drillPath.length > 0 && (
          <button
            className="icon-button"
            onClick={() => setDrillPath((path) => path.slice(0, -1))}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Back
          </button>
        )}
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {drillPath.length > 0
            ? drillPath.map((n) => n.name).join(' › ')
            : 'Top level'}
        </span>

        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Size
          </label>
          <select
            className="control"
            value={sizeBy}
            onChange={(e) => setSizeBy(e.target.value as SizeMetric)}
          >
            <option value="duration">Duration</option>
            <option value="activities">Activity count</option>
            {hasCost && <option value="cost">Budget cost</option>}
          </select>

          <label className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Colour
          </label>
          <select
            className="control"
            value={colourBy}
            onChange={(e) => setColourBy(e.target.value as ColourMetric)}
          >
            <option value="float">Mean float</option>
            <option value="progress">Progress</option>
            <option value="critical">Has critical work</option>
          </select>
        </div>
      </div>

      <div ref={ref} className="w-full">
        {width > 0 && tiles.length > 0 && (
          <svg width={width} height={height} role="img" aria-label="WBS treemap">
            {tiles.map(({ node, x, y, width: w, height: h }) => {
              const showLabel = w > 62 && h > 26;
              return (
                <g
                  key={node.id}
                  className="chart-hit"
                  onMouseMove={(event) =>
                    tooltip.show(
                      <div>
                        <div className="font-semibold">{node.name}</div>
                        {node.code && (
                          <div style={{ color: 'var(--text-muted)' }}>{node.code}</div>
                        )}
                        <div className="tnum" style={{ marginTop: 4 }}>
                          {node.activityCount} activities ·{' '}
                          {Math.round(node.durationDays)}d
                          {hasCost && ` · ${formatCurrency(node.budgetCost)}`}
                        </div>
                        <div className="tnum">
                          Mean float {node.meanFloatDays.toFixed(0)}d ·{' '}
                          {node.criticalCount} critical
                        </div>
                        <div className="tnum">
                          {node.percentComplete.toFixed(0)}% complete
                        </div>
                        <div style={{ color: 'var(--mr-vivid-blue)', marginTop: 4 }}>
                          {node.children.length > 0
                            ? 'Click to drill in'
                            : 'Click to filter'}
                        </div>
                      </div>,
                      event
                    )
                  }
                  onMouseLeave={tooltip.hide}
                  onClick={() => {
                    if (node.children.some((child) => valueOf(child) > 0)) {
                      setDrillPath((path) => [...path, node]);
                    } else {
                      onSelectNode?.(node.activityIds, node.name);
                    }
                  }}
                >
                  <rect
                    x={x + 1}
                    y={y + 1}
                    width={Math.max(0, w - 2)}
                    height={Math.max(0, h - 2)}
                    fill={colourFor(node)}
                    fillOpacity={0.85}
                    stroke="var(--surface-page)"
                    strokeWidth="1"
                    rx={1}
                  />
                  {showLabel && (
                    <>
                      <text
                        x={x + 8}
                        y={y + 16}
                        style={{
                          fill: '#fff',
                          fontSize: 11,
                          fontWeight: 600,
                          pointerEvents: 'none',
                        }}
                      >
                        {node.name.length > Math.floor(w / 7)
                          ? `${node.name.slice(0, Math.floor(w / 7) - 1)}…`
                          : node.name}
                      </text>
                      {h > 40 && (
                        <text
                          x={x + 8}
                          y={y + 30}
                          className="tnum"
                          style={{
                            fill: 'rgba(255,255,255,0.75)',
                            fontSize: 9.5,
                            pointerEvents: 'none',
                          }}
                        >
                          {sizeBy === 'cost'
                            ? formatCurrency(node.budgetCost)
                            : sizeBy === 'duration'
                              ? `${formatCompact(node.durationDays)}d`
                              : `${node.activityCount} act`}
                        </text>
                      )}
                    </>
                  )}
                </g>
              );
            })}
          </svg>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
        {colourBy === 'float' && (
          <>
            <LegendSwatch colour="var(--status-fail)" label="Negative float" />
            <LegendSwatch colour="var(--data-1)" label="0–1d" />
            <LegendSwatch colour="var(--data-3)" label="2–25d" />
            <LegendSwatch colour="var(--data-4)" label="26–44d" />
            <LegendSwatch colour="var(--status-warn)" label="> 44d" />
          </>
        )}
        {colourBy === 'progress' && (
          <>
            <LegendSwatch colour="var(--data-1)" label="Not started" />
            <LegendSwatch colour="var(--data-3)" label="In progress" />
            <LegendSwatch colour="var(--data-5)" label="Near complete" />
          </>
        )}
        {colourBy === 'critical' && (
          <>
            <LegendSwatch colour="var(--status-fail)" label="Contains critical work" />
            <LegendSwatch colour="var(--data-3)" label="No critical work" />
          </>
        )}
      </div>
    </div>
  );
}

function LegendSwatch({ colour, label }: { colour: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className="inline-block h-2.5 w-2.5 rounded-sm"
        style={{ background: colour }}
      />
      {label}
    </span>
  );
}
